/**
 * Orbit display currency (replaces the Bucks app).
 *
 * Every market sells in BRL, so this only changes what the shopper reads: each
 * [data-orbit-money] span (snippets/orbit-money.liquid holds the exact cents) is shown in
 * the display currency, and the "charged in BRL" note (snippets/orbit-currency-notice.liquid)
 * is filled in. Checkout stays in BRL.
 *
 * Display currency, first match wins:
 *   ?orbit_currency=XXX (QA, this tab only) → the shopper's saved choice
 *   (localStorage.orbit_currency, written by the region panel and the header selector)
 *   → the setting: the visitor's detected country, the store's country, or BRL.
 * Rates: open.er-api.com (166 currencies), then the ECB via Frankfurter, cached in
 * localStorage for the configured hours; with no rate the page simply stays in BRL.
 *
 * Price markup is replaced all the time (variant picker, cart drawer morphs, quick add,
 * facets, infinite scroll), and a morph always resets a converted span to its BRL text and
 * drops data-orbit-done, so one MutationObserver re-converts whatever comes back.
 */
import { formatMoney } from '@theme/money-formatting';

const LOCALE_DATA = readJSON('OrbitLocaleData');
const MONEY = readJSON('OrbitMoneyConfig') || {};
const STORE = MONEY.store || 'BRL';
const html = document.documentElement;
const params = new URLSearchParams(location.search);
const QA = (params.get('orbit_currency') || '').toUpperCase();
const SAVED_KEY = 'orbit_currency';
const FX_KEY = 'orbit_fx';
const SKIP = 'input, textarea, select, option, script, template, [data-orbit-money-skip], facet-status-component, price-facet-component, facet-remove-component';

const settings = LOCALE_DATA?.currency || {};
const list = new Set([STORE, ...(settings.list || [])]);
if (/^[A-Z]{3}$/.test(QA)) list.add(QA);

let active = STORE;
let rate = 1;
let observer = null;
let scheduled = false;

function readJSON(id) {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || 'null');
  } catch {
    return null;
  }
}

function readSaved() {
  try {
    const saved = JSON.parse(localStorage.getItem(SAVED_KEY) || 'null');
    return saved && /^[A-Z]{3}$/.test(saved.code) ? saved : null;
  } catch {
    return null;
  }
}

function save(code, mode) {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify({ code, mode, ts: Date.now() }));
  } catch {}
}

function bucksActive() {
  return Boolean(
    document.querySelector('script[src*="bucks-currency-converter"], script[src*="widgetLoader"], .buckscc-converted') ||
      window.BucksCC
  );
}

/* ── Rates ─────────────────────────────────────────────────────────────── */

function readCache() {
  try {
    const cache = JSON.parse(localStorage.getItem(FX_KEY) || 'null');
    return cache && cache.base === STORE && cache.rates ? cache : null;
  } catch {
    return null;
  }
}

async function fetchRates() {
  const sources = [
    async () => {
      const response = await fetch(`https://open.er-api.com/v6/latest/${STORE}`);
      const json = await response.json();
      if (json.result !== 'success' || !json.rates) throw new Error('open.er-api');
      return { rates: json.rates, source: 'open.er-api.com' };
    },
    async () => {
      const response = await fetch(`https://api.frankfurter.dev/v1/latest?base=${STORE}`);
      const json = await response.json();
      if (!json.rates) throw new Error('frankfurter');
      return { rates: json.rates, source: 'frankfurter' };
    },
  ];
  for (const source of sources) {
    try {
      const { rates, source: name } = await source();
      const kept = {};
      for (const code of list) if (typeof rates[code] === 'number') kept[code] = rates[code];
      const cache = { base: STORE, ts: Date.now(), source: name, rates: kept };
      try {
        localStorage.setItem(FX_KEY, JSON.stringify(cache));
      } catch {}
      return cache;
    } catch {}
  }
  return null;
}

async function getRate(code) {
  if (code === STORE) return 1;
  const cache = readCache();
  const maxAge = (Number(settings.cacheHours) || 24) * 3600 * 1000;
  const fresh = cache && Date.now() - cache.ts < maxAge && cache.rates[code];
  if (fresh) return cache.rates[code];
  const fetched = await fetchRates();
  return fetched?.rates[code] || cache?.rates[code] || null;
}

/* ── Formatting ────────────────────────────────────────────────────────── */

let formatter = null;
function format(cents) {
  const value = (cents / 100) * rate;
  if (!formatter || formatter.code !== active) {
    const locale = (window.Shopify && Shopify.locale) || MONEY.locale || html.lang || 'pt-BR';
    let intl;
    try {
      intl = new Intl.NumberFormat(locale, { style: 'currency', currency: active, currencyDisplay: 'narrowSymbol' });
    } catch {
      intl = new Intl.NumberFormat(locale, { style: 'currency', currency: active });
    }
    formatter = { code: active, intl };
  }
  return `${settings.approx === false ? '' : '≈ '}${formatter.intl.format(value)} ${active}`;
}

function storeText(el, cents) {
  const template = el.hasAttribute('data-orbit-code') ? MONEY.formatWithCode : MONEY.format;
  return template ? formatMoney(cents, template, STORE) : el.textContent;
}

function convert(el) {
  if (el.closest(SKIP)) return;
  const cents = Number(el.dataset.orbitMoney);
  if (!Number.isFinite(cents)) return;
  if (active === STORE) {
    if (el.dataset.orbitDone) {
      el.textContent = storeText(el, cents);
      el.removeAttribute('title');
      delete el.dataset.orbitDone;
    }
    return;
  }
  if (!el.dataset.orbitDone) el.title = `${el.textContent.trim()} (${STORE})`;
  el.textContent = format(cents);
  el.dataset.orbitDone = active;
}

function syncNotices() {
  for (const notice of document.querySelectorAll('[data-orbit-currency-notice]')) {
    const converted = notice.querySelector('[data-orbit-notice="converted"]');
    const brl = notice.querySelector('[data-orbit-notice="brl"]');
    const foreign = active !== STORE;
    if (converted) converted.hidden = !foreign;
    if (brl) brl.hidden = foreign;
    for (const code of notice.querySelectorAll('[data-orbit-currency-code]')) code.textContent = active;
    notice.hidden = false;
  }
}

/* "= R$ 1.749,00 BRL" under a converted product price, from that price's exact cents. */
function syncEquivalents() {
  for (const line of document.querySelectorAll('[data-orbit-brl-equiv]')) {
    const host = line.closest('product-price') || line.parentElement;
    const price =
      host?.querySelector('.price [data-orbit-money]') ||
      [...(host?.querySelectorAll('[data-orbit-money]') || [])].find((el) => !el.closest('.compare-at-price, .unit-price'));
    const cents = Number(price?.dataset.orbitMoney);
    const show = active !== STORE && Number.isFinite(cents) && Boolean(MONEY.formatWithCode);
    const text = show ? `= ${formatMoney(cents, MONEY.formatWithCode, STORE)}` : '';
    if (line.textContent !== text) line.textContent = text;
    if (line.hidden === show) line.hidden = !show;
  }
}

function syncSelects() {
  for (const select of document.querySelectorAll('[data-orbit-currency-options]')) {
    if (select.dataset.orbitLabelled !== '1') {
      for (const option of select.options) {
        try {
          const symbol = new Intl.NumberFormat('en', { style: 'currency', currency: option.value, currencyDisplay: 'symbol' })
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value;
          if (symbol && symbol !== option.value) option.textContent = `${option.value} · ${symbol}`;
        } catch {}
      }
      select.dataset.orbitLabelled = '1';
    }
    if (select.matches('[data-orbit-currency-select]')) select.value = active;
  }
}

function flush() {
  scheduled = false;
  observer?.disconnect();
  const selector = active === STORE ? '[data-orbit-money][data-orbit-done]' : `[data-orbit-money]:not([data-orbit-done="${active}"])`;
  for (const el of document.querySelectorAll(selector)) convert(el);
  syncNotices();
  syncEquivalents();
  observer?.takeRecords();
  if (observer) observe();
}

function schedule() {
  if (scheduled) return;
  scheduled = true;
  // A hidden tab gets no animation frames; a timer keeps it converting there too.
  if (document.hidden) setTimeout(flush, 50);
  else requestAnimationFrame(flush);
}

function observe() {
  observer.observe(html, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['data-orbit-money', 'data-orbit-done', 'hidden'],
  });
}

function apply(code, nextRate) {
  active = code;
  rate = nextRate;
  html.setAttribute('data-orbit-currency', code);
  html.removeAttribute('data-orbit-currency-pending');
  window.orbit = window.orbit || {};
  window.orbit.currency = { code, rate, store: STORE };
  // Kept in BRL too: a cart morph re-hides the "charged in BRL" note, which flush re-shows.
  if (!observer) observer = new MutationObserver(schedule);
  flush();
  syncSelects();
  document.dispatchEvent(new CustomEvent('orbit:currency-change', { detail: { code, rate } }));
}

/** Switch the display currency (the region panel and header selector call this). */
async function set(code, mode = 'manual') {
  code = String(code || STORE).toUpperCase();
  if (/^[A-Z]{3}$/.test(code) && mode === 'manual') list.add(code);
  if (!list.has(code)) code = STORE;
  if (mode) save(code, mode);
  const nextRate = await getRate(code);
  if (!nextRate) {
    console.warn(`[orbit-currency] no exchange rate for ${code}; prices stay in ${STORE}`);
    apply(STORE, 1);
    return;
  }
  apply(code, nextRate);
}

async function resolve() {
  if (/^[A-Z]{3}$/.test(QA)) return QA;
  const saved = readSaved();
  if (saved?.mode === 'manual') return saved.code;
  if (LOCALE_DATA?.designMode) return STORE;
  const mode = settings.default || 'detected';
  if (mode === 'brl') return STORE;
  if (mode === 'market') {
    const local = window.orbit?.geo?.currencyOf?.(LOCALE_DATA?.current?.country);
    if (local) list.add(local);
    return local || STORE;
  }
  const detected = await Promise.race([
    window.orbit?.geo?.detect?.() ?? Promise.resolve(null),
    new Promise((resolve) => setTimeout(() => resolve(null), 1500)),
  ]);
  const country = detected || LOCALE_DATA?.current?.country;
  const local = window.orbit?.geo?.currencyOf?.(country);
  if (local) list.add(local);
  return local || saved?.code || STORE;
}

async function start() {
  window.orbit = window.orbit || {};
  window.orbit.setCurrency = set;
  const enabled = settings.enabled || /^[A-Z]{3}$/.test(QA);
  if (!enabled || !LOCALE_DATA) {
    html.removeAttribute('data-orbit-currency-pending');
    return;
  }
  if (bucksActive()) {
    console.warn('[orbit-currency] the Bucks currency app is still active; standing down to avoid double conversion');
    html.removeAttribute('data-orbit-currency-pending');
    html.removeAttribute('data-orbit-currency');
    return;
  }

  // A failsafe: whatever happens below, BRL prices never stay hidden.
  setTimeout(() => html.removeAttribute('data-orbit-currency-pending'), 1200);

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-orbit-currency-select]');
    if (select) set(select.value, 'manual');
  });

  const code = await resolve();
  const mode = /^[A-Z]{3}$/.test(QA) ? null : readSaved()?.mode === 'manual' ? 'manual' : 'auto';
  await set(code, mode);
}

start();
